// ============================================================
// Project manager (Brief v2 · Tasks 2 & 4).
//
// Turns the single-tenant app into a multi-project one from the client side:
//   * listMyProjects()  — projects the signed-in user belongs to (RPC, RLS-safe)
//   * createProject()   — self-service onboarding (creator becomes qcmanager)
//   * inviteMember()    — add a teammate by email + role
//   * switchProject()   — re-point the data layer at another project and
//                         hot-reload the engine without a full page refresh
//
// Project scoping itself lives in persistence.ts (getProjectId/setProjectId +
// loadProjectIntoEngine); this module is the orchestration + RPC surface.
// ============================================================
import { supabase } from "./supabase";
import { eng } from "./engineApi";
import { applyCanonicalTopology } from "./topology";
import {
  DEMO_PROJECT_ID,
  getProjectId,
  setProjectId,
  loadProjectIntoEngine,
} from "./persistence";

export interface ProjectSummary {
  id: string;
  name: string;
  location: string | null;
  client: string | null;
  epc: string | null;
  itp_id: string | null;
  status: string;
  role: string;
  member_count: number;
}

const DEMO_FALLBACK: ProjectSummary = {
  id: DEMO_PROJECT_ID,
  name: "Ballyroe Solar Farm",
  location: "Ballyhea, Charleville, Co. Cork, Ireland",
  client: "ILOS Projects GmbH",
  epc: "Conecon GmbH",
  itp_id: "ITP-BAL · Rev B",
  status: "active",
  role: "qcmanager",
  member_count: 1,
};

/** Projects the current user can open. Falls back to the demo project when
 *  unauthenticated / offline so the switcher always has at least one entry. */
export async function listMyProjects(): Promise<ProjectSummary[]> {
  if (!supabase) return [DEMO_FALLBACK];
  try {
    const { data, error } = await supabase.rpc("list_my_projects");
    if (error || !data || data.length === 0) return [DEMO_FALLBACK];
    return data as ProjectSummary[];
  } catch {
    return [DEMO_FALLBACK];
  }
}

/** Create a new project. Returns its id. The creator becomes its QC manager. */
export async function createProject(p: {
  name: string;
  location: string;
  client: string;
  epc: string;
  itpId: string;
}): Promise<string> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase.rpc("create_project", {
    p_name: p.name,
    p_location: p.location,
    p_client: p.client,
    p_epc: p.epc,
    p_itp_id: p.itpId,
  });
  if (error) throw error;
  return data as string;
}

/** Upload a project's CAD DXF to storage and queue it for parsing. The actual
 *  DXF→topo_* parse is a worker job (tools/dxf/ingest_project.py); this captures
 *  the file + records the queued import so the map can be populated later. */
export async function uploadProjectDxf(projectId: string, file: File): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const path = `${projectId}/${file.name}`;
  const { error } = await supabase.storage.from("project-dxf").upload(path, file, { upsert: true });
  if (error) throw error;
  await supabase.rpc("queue_dxf_import", { p_project: projectId, p_path: path, p_name: file.name });
}

/** Invite a teammate by email + role. Routes through the `invite-user` Edge
 *  Function, which records membership/pending-invite AND emails a magic-link to
 *  brand-new addresses (Task 4). Falls back to the RPC (no email) if the
 *  function is unavailable, so existing-account invites still work. */
export async function inviteMember(
  projectId: string,
  email: string,
  role: string,
): Promise<{ resolved: boolean; email: string; role: string; emailed?: boolean }> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase.functions.invoke("invite-user", {
    body: { projectId, email, role },
  });
  if (!error && data && !(data as { error?: string }).error) {
    return data as { resolved: boolean; email: string; role: string; emailed?: boolean };
  }
  // Fallback: direct RPC (no magic-link email, but membership/pending still set).
  const surfaced = (data as { error?: string } | null)?.error;
  const { data: rpcData, error: rpcError } = await supabase.rpc("invite_member", {
    p_project: projectId,
    p_email: email,
    p_role: role,
  });
  if (rpcError) throw new Error(surfaced || rpcError.message);
  return rpcData as { resolved: boolean; email: string; role: string };
}

export interface ProjectMember {
  user_id: string;
  full_name: string | null;
  email: string;
  role: string;
  accepted_at: string | null;
}

/** List a project's members (name + email + role). QC-manager view. */
export async function listMembers(projectId: string): Promise<ProjectMember[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc("list_project_members", { p_project: projectId });
  if (error) throw error;
  return (data || []) as ProjectMember[];
}

/** Change a member's role (QC-manager only; can't demote the last QC manager). */
export async function updateMemberRole(projectId: string, userId: string, role: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase.rpc("update_member_role", {
    p_project: projectId,
    p_user: userId,
    p_role: role,
  });
  if (error) throw error;
}

/** Remove a member (QC-manager only; can't remove the last QC manager). */
export async function removeMember(projectId: string, userId: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase.rpc("remove_member", { p_project: projectId, p_user: userId });
  if (error) throw error;
}

/** Switch the active project and hot-reload the engine in place. */
export async function switchProject(id: string): Promise<void> {
  if (id === getProjectId()) return;
  setProjectId(id);
  await loadProjectIntoEngine();
  // Re-hydrate the engine from the freshly-staged buffer, then re-apply the
  // canonical CAD topology (global today — per-project DXF ingestion is the
  // queued onboarding job) and notify React to re-render.
  eng().loadFromStorage();
  await applyCanonicalTopology();
  try {
    (window as any).__gpNotify?.();
  } catch {
    /* ignore */
  }
}

export { getProjectId, DEMO_PROJECT_ID };
